/**
 * TipOfTheDay — full-window overlay that surfaces one rotating product tip
 * per session. Dimmed/blurred backdrop with a centered gradient hero card.
 * Mounted once at App level.
 *
 * Closes via ×, backdrop click, Escape, or a 20s auto-dismiss (the timer
 * pauses while the pointer is over the card).
 */
import React, { useEffect, useRef } from "react";
import { useTipOfTheDay } from "./useTipOfTheDay";
import { TIPS } from "./tips";
import "./TipOfTheDay.css";

const AUTO_DISMISS_MS = 20000;

export default function TipOfTheDay() {
  const { tip, visible, next, dismiss, disable } = useTipOfTheDay();
  const timerRef = useRef(null);

  const armTimer = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(dismiss, AUTO_DISMISS_MS);
  };

  // Auto-dismiss — re-armed whenever a new tip becomes visible.
  useEffect(() => {
    if (visible && tip) armTimer();
    return () => clearTimeout(timerRef.current);
  }, [visible, tip]);

  // Escape closes the overlay.
  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, dismiss]);

  if (!tip) return null;

  const position = TIPS.indexOf(tip) + 1;

  return (
    <div
      className={`tip-otd-backdrop ${visible ? "tip-otd-backdrop--open" : ""}`}
      onClick={dismiss}
      role="presentation"
      data-evo-id="tip-of-the-day-1"
    >
      <div
        className="tip-otd-card"
        role="dialog"
        aria-label="Dica do dia"
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={() => clearTimeout(timerRef.current)}
        onMouseLeave={() => {
          if (visible) armTimer();
        }}
        data-evo-id="tip-of-the-day-2"
      >
        <div className="tip-otd-card__glow" aria-hidden="true" />
        <button
          className="tip-otd-card__close"
          onClick={dismiss}
          aria-label="Fechar"
          title="Fechar"
          data-evo-id="tip-of-the-day-close"
        >
          ×
        </button>

        <div className="tip-otd-card__header">
          <div className="tip-otd-card__badge" aria-hidden="true">
            💡
          </div>
          <div className="tip-otd-card__heading">
            <div className="tip-otd-card__eyebrow">Dica do dia</div>
            <h2 className="tip-otd-card__title">{tip.title}</h2>
          </div>
        </div>
        <p className="tip-otd-card__body">{tip.body}</p>

        <div className="tip-otd-card__footer">
          <span className="tip-otd-card__counter">
            {position} / {TIPS.length}
          </span>
          <div className="tip-otd-card__actions">
            <button
              className="tip-otd-card__btn tip-otd-card__btn--ghost"
              onClick={disable}
              data-evo-id="tip-of-the-day-disable"
            >
              Não mostrar
            </button>
            <button
              className="tip-otd-card__btn tip-otd-card__btn--solid"
              onClick={next}
              data-evo-id="tip-of-the-day-next"
            >
              Próxima dica
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
